import { Link } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { ACCESS_REQUEST_RESOURCE_NAME_MAX } from '@infras/shared-types';
import { ACCESS_REVIEWERS, byLayer } from '../lib/access';
import type { AccessReviewer } from '../lib/access';

/** What each layer is FOR. The roster itself is live; only these role names are
 *  editorial, and they describe the layer, not the people in it. */
const LAYER_ROLE: Record<number, string> = {
  1: 'First responders',
  2: 'Escalation',
  3: 'Final escalation',
};
const LAYER_HINT: Record<number, string> = {
  1: 'triage every request',
  2: 'more context / authority',
};

/**
 * Docs: how any staff member requests access to a company resource. Rendered as
 * a section of the /docs sidebar. Mirrors the real flow (AccessRequestNewPage +
 * the access-requests resolver lifecycle) so it can't drift far.
 */
export function RequestAccessDocsPage() {
  const { data } = useQuery<{ accessReviewers: AccessReviewer[] }>(ACCESS_REVIEWERS);
  const layers = byLayer(data?.accessReviewers ?? []);

  return (
    <div className="page">
      <div className="detail-head">
        <h1>Requesting access</h1>
        <span className="badge">everyone</span>
      </div>
      <p className="detail-desc">
        Need a <strong>database</strong>, Grafana org, GCP project, or other tool? File it here —
        reviewers, timeline, clear approved/denied. <strong>Anyone signed in can request</strong>.
        <br />
        <span className="muted">
          Skip <strong>GitLab repos</strong> — engineers get Developer by default.
        </span>
      </p>

      <h3 className="section">1 · Open a request</h3>
      <p className="detail-desc">
        <Link to="/access">Access</Link> → <strong>+ New request</strong>, or go straight to{' '}
        <Link to="/access/new">the form</Link>. <em>My requests</em> tracks what you've filed.
      </p>

      <h3 className="section">2 · Answer four questions</h3>
      <ul className="docs-prose">
        <li>
          <strong>Which system?</strong> — one of twelve, grouped (data &amp; secrets,
          infrastructure, code &amp; CI, cloud &amp; network, apps &amp; tooling). Each option
          names its provider, so &ldquo;Database&rdquo; reads as{' '}
          <em>Database — MongoDB Atlas / DO Postgres</em>.
        </li>
        <li>
          <strong>Which one?</strong> <span className="muted">(optional)</span> — the precise
          target (<code>signagecloud-qa</code>, <code>grafana-v2.opstech.org</code>, a GCP project
          id), max <strong>{ACCESS_REQUEST_RESOURCE_NAME_MAX} characters</strong>. Leave it blank if
          the reason already says it; the request is then titled with the system&rsquo;s own name.
        </li>
        <li>
          <strong>How long?</strong> — <strong>Permanent</strong>, or{' '}
          <strong>Temporary</strong> with a window (1, 7, 30, 90, 180 days, 1 or 2 years). Temporary
          records a real expiry date. Prefer it: a permanent grant to a sensitive system is filed as{' '}
          <strong>high risk</strong> and gets a second look.
        </li>
        <li>
          <strong>Why do you need it?</strong> <span className="muted">(required)</span> — what
          you&rsquo;re doing. This is where a reviewer learns what you actually need, so name the
          exact resource here if you skipped the field above.
        </li>
      </ul>
      <p className="detail-desc">
        The panel beside the form shows what you are about to file — system, target, duration,
        expiry and the review path — plus the live reviewer layers.{' '}
        <strong>File request</strong> opens the request&rsquo;s page.
      </p>
      <p className="detail-desc muted">
        The old form also asked for a maintainer and a set of admin-configured questions. Both are
        gone: the reason field covers them, and requests filed before this change still show their
        answers on the detail page.
      </p>

      <h3 className="section">3 · What happens next</h3>
      <ol className="docs-prose">
        <li><strong>Layer&nbsp;1</strong> is assigned and notified (Slack + email).</li>
        <li>They may <strong>ask questions</strong> or request <strong>maintainer sign-off</strong>.</li>
        <li>If unresolved, they <strong>escalate</strong> to the next layer.</li>
        <li>A reviewer grants access outside this app, then marks <strong>approved</strong> or <strong>denied</strong>. You're emailed either way.</li>
      </ol>
      <p className="docs-note">
        "Approved" means the grant was recorded — permission is applied in the target system, not here.
      </p>

      <h3 className="section">Who reviews — the layers</h3>
      <p className="detail-desc">
        Layered reviewer roster. <strong>Layer&nbsp;1</strong> triages every request; escalate up if needed.
      </p>
      {/* Reads the LIVE roster. This list used to be hardcoded prose ("Binh, Kevin,
          Ted") sitting a few hundred pixels from a form that renders the same
          roster from the database — so the docs were wrong the first time anyone
          was added or removed, and the note below admitted it was "a snapshot".
          A page whose whole job is telling you who to expect should not need
          re-editing when a reviewer changes. */}
      {layers.length === 0 ? (
        <p className="muted">No reviewers configured yet — ask a root user to set the roster up.</p>
      ) : (
        <ul className="docs-prose docs-prose--loose">
          {layers.map(([layer, list]) => (
            <li key={layer}>
              <span className="tag-badge">L{layer}</span>{' '}
              <strong>{LAYER_ROLE[layer] ?? `Layer ${layer}`}</strong>{' '}
              {LAYER_HINT[layer] && <span className="muted">({LAYER_HINT[layer]})</span>} —{' '}
              {list.map((r) => r.email).join(', ')}
            </li>
          ))}
        </ul>
      )}
      <p className="docs-note">
        Managed in Settings → Access reviewers; the{' '}
        <Link to="/access/new">request form</Link> shows the same roster.
      </p>

      <h3 className="section">4 · Track &amp; respond</h3>
      <ul className="docs-prose">
        <li><Link to="/access">Access → My requests</Link> — status, answers, timeline.</li>
        <li><strong>Comment</strong> with rich text / <code>@mention</code> to answer reviewers
          (mentions email the teammate via SES when email is configured).</li>
        <li><strong>Cancel</strong> while pending if you no longer need it.</li>
        <li>
          {/* The real route, not the /mcp redirect — an in-app link should not
              cost a navigation to arrive where it already knows it is going. */}
          <strong>From an agent</strong> — <Link to="/docs/mcp">infras MCP</Link> can file, list, read,
          and comment; approve/deny stays in this dashboard.
        </li>
      </ul>

      <div className="dep-card docs-tip">
        <strong>Tip:</strong> precise resource name + reason = faster review. Urgent?{' '}
        <code>@mention</code> a maintainer or reviewer — they get an email when SES is enabled.
      </div>
    </div>
  );
}
